// src/components/Sidebar.jsx
import React, { useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LineChart,
  Package,
  ShoppingCart,
  Receipt,
  Wallet,
  Users,
  Settings,
  LogIn,
  ChevronDown,
  Clipboard,
  Shield,
  Calculator,
  FolderKanban,
  Briefcase,
  CalendarDays,
  Bell,
  Star,
  Search,
  Plus,
  Minus,
  UserCog,
} from 'lucide-react'
import { MODULES } from '../modules/registry.js'

export default function Sidebar({ connected, onLogout, user, hasRole, modules, isModuleEnabled, preferences, updatePreferences }) {
  const navigate = useNavigate()

  const [open, setOpen] = useState({
    favoris: true,
    pilotage: true,
    ops: true,
    ventes: true,
    rh: true,
    compta: true,
    admin: true,
    security: true,
  })
  const [showMore, setShowMore] = useState({
    favoris: false,
    pilotage: false,
    ventes: false,
    ops: false,
    rh: false,
    compta: false,
    security: false,
    admin: false,
  })
  const [search, setSearch] = useState('')
  const favorites = preferences?.sidebar?.favorites || []
  const compact = !!preferences?.sidebar?.compact
  const updateSidebarPrefs = (patch) => {
    if (typeof updatePreferences === 'function') {
      updatePreferences({ sidebar: { ...(preferences?.sidebar || {}), ...patch } })
    }
  }

  const baseLink =
    'group relative flex items-center gap-2 w-full rounded-lg px-3 py-2 text-[13px] leading-5 transition-all select-none'
  const inactive =
    'text-slate-700 hover:bg-slate-50 hover:text-slate-900 active:scale-[0.99]'
  const active =
    'bg-slate-50 text-slate-800 shadow-sm ring-1 ring-slate-100'
  const leftAccent =
    'before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-slate-900'

  const SectionTitle = ({ title, id, icon: Icon }) => (
    <button
      type="button"
      onClick={() => setOpen(s => ({ ...s, [id]: !s[id] }))}
      className="w-full flex items-center justify-between px-3 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600 transition-colors"
    >
      <span className="flex items-center gap-2">
        {Icon ? <Icon size={14} className="text-slate-300" /> : null}
        {title}
      </span>
      <ChevronDown
        size={14}
        className={`transition-transform ${open[id] ? 'rotate-180' : 'rotate-0'}`}
      />
    </button>
  )

  function LinkItem({ to, label, icon: Icon, visible = true, end = false, moduleKey }) {
    if (!visible) return null

    const isFav = moduleKey && favorites.includes(moduleKey)
    return (
      <NavLink
        to={to}
        end={end}
        title={label}
        className={({ isActive }) =>
          `${baseLink} ${isActive ? `${active} ${leftAccent}` : inactive}`
        }
      >
        {Icon && (
          <Icon
            size={16}
            className="text-slate-400 group-hover:text-slate-600 transition-colors"
          />
        )}
        {!compact && <span className="flex-1">{label}</span>}
        {!compact && moduleKey && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const next = favorites.includes(moduleKey)
                ? favorites.filter((k) => k !== moduleKey)
                : [...favorites, moduleKey]
              updateSidebarPrefs({ favorites: next })
            }}
            className={`p-1 rounded ${isFav ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500'}`}
            aria-label="Favori"
          >
            <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        )}
      </NavLink>
    )
  }

  // ✅ Rôles
  const isAdmin = connected && hasRole('admin')
  const isOps = connected && hasRole('ops')
  const isSales = connected && hasRole('sales')
  const isAuditor = connected && hasRole('auditor')
  const isSuperAdmin = connected && hasRole('superadmin')

  const roleOk = (allowed) => {
    if (!connected) return false
    if (!allowed || !allowed.length) return true
    return allowed.some((r) => hasRole(r))
  }

  const moduleOk = (key) => {
    if (!key) return true
    if (typeof isModuleEnabled === 'function') return isModuleEnabled(key)
    if (modules && modules[key]) return modules[key].enabled !== false
    return true
  }

  const items = useMemo(() => {
    const map = {
      analytics: LineChart,
      sales: Wallet,
      orders: ShoppingCart,
      customers: Users,
      products: Package,
      purchases: ShoppingCart,
      expenses: Receipt,
      projects: FolderKanban,
      prestations: Briefcase,
      agenda: CalendarDays,
      relances: Bell,
      finance: Calculator,
      audit: Clipboard,
      employees: UserCog,
    }

    const base = MODULES.map((m) => ({
      ...m,
      icon: map[m.key],
      moduleKey: m.key,
    }))

    // Items non modulaires
    base.push({
      key: 'settings',
      label: 'Paramètres',
      category: 'admin',
      route: '/settings',
      roles: ['admin', 'superadmin'],
      priority: 'primary',
      icon: Settings,
      moduleKey: null,
    })

    return base
  }, [])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (!roleOk(it.roles)) return false
      if (!moduleOk(it.moduleKey || it.key)) return false
      if (!q) return true
      return it.label.toLowerCase().includes(q)
    })
  }, [items, search, connected, user, modules, favorites, hasRole, isModuleEnabled])

  const favoritesItems = filteredItems.filter((i) => i.moduleKey && favorites.includes(i.moduleKey))

  const renderSection = (categoryKey, title, icon) => {
    const sectionItems = filteredItems.filter((i) => i.category === categoryKey)
    if (!sectionItems.length) return null

    const showAll = search.trim() ? true : showMore[categoryKey]
    const primary = sectionItems.filter((i) => i.priority === 'primary')
    const secondary = sectionItems.filter((i) => i.priority !== 'primary')
    const visibleItems = showAll ? sectionItems : [...primary, ...secondary.slice(0, 2)]
    const hiddenCount = sectionItems.length - visibleItems.length

    return (
      <>
        {!compact && <SectionTitle title={title} id={categoryKey} icon={icon} />}
        <div
          className={`grid gap-1 overflow-hidden transition-[max-height,opacity] duration-200 ${
            open[categoryKey] ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          {visibleItems.map((item) => (
            <LinkItem
              key={item.key}
              to={item.route}
              label={item.label}
              icon={item.icon}
              moduleKey={item.moduleKey || item.key}
            />
          ))}
          {!compact && !search.trim() && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowMore((s) => ({ ...s, [categoryKey]: !s[categoryKey] }))}
              className="mx-2 my-1 flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-700"
            >
              {showAll ? <Minus size={12} /> : <Plus size={12} />}
              {showAll ? 'Moins' : `Plus (${hiddenCount})`}
            </button>
          )}
        </div>
      </>
    )
  }

  return (
    <aside className={`${compact ? 'w-20' : 'w-64 min-w-64'} relative isolate bg-white border-r border-slate-200 flex flex-col`}>
      {/* Nav */}
      <nav className="relative z-10 flex-1 overflow-y-auto px-2 pb-2">
        {connected && !compact && (
          <div className="px-2 pt-3 pb-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un module..."
                className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-2 py-1.5 text-[12px]"
              />
            </div>
          </div>
        )}

        {connected && favoritesItems.length > 0 && !compact && (
          <>
            <SectionTitle title="Favoris" id="favoris" />
            <div className="grid gap-1">
              {favoritesItems.map((item) => (
                <LinkItem
                  key={`fav-${item.key}`}
                  to={item.route}
                  label={item.label}
                  icon={item.icon}
                  moduleKey={item.moduleKey || item.key}
                />
              ))}
            </div>
          </>
        )}

        {renderSection('pilotage', 'Pilotage', LineChart)}
        {renderSection('ops', 'Opérations', Package)}
        {renderSection('ventes', 'Ventes', Wallet)}
        {renderSection('rh', 'RH & Interne', UserCog)}
        {renderSection('compta', 'Comptabilité', Calculator)}
        {renderSection('security', 'Sécurité', Shield)}
        {renderSection('admin', 'Administration', Settings)}

        {!connected && (
          <div className="mt-2">
            <div className="px-3 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Session
            </div>
            <button
              className={`${baseLink} ${inactive} w-full`}
              onClick={() => navigate('/auth')}
              type="button"
            >
              <LogIn size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
              <span className="flex-1">Connexion</span>
            </button>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-slate-100">
        {connected ? (
          <button
            type="button"
            onClick={() => updateSidebarPrefs({ compact: !compact })}
            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
          >
            {compact ? 'Mode normal' : 'Mode compact'}
          </button>
        ) : (
          <div className="text-[11px] text-slate-400 text-center">
            FEDIS • v1
          </div>
        )}
      </div>
    </aside>
  )
}
